// src/components/SimpleComponents/SimpleNipc.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import React, { useMemo, useRef, useCallback } from 'react';
import { Form, Input, Modal } from 'antd';
import { searchNipc } from 'services/nipc-external/nipcService';
import SimpleFormItem from './SimpleFormItem';
import useFormElement from 'hooks/useFormElement';
import { returnOnlyNumbers, applyNumberMask } from 'utils/helpers';
import { isValidNipc } from 'utils/validation';
import useCustomValidationStatus from 'hooks/useCustomValidationStatus';
import { useSimpleFormContext } from './SimpleFormProvider';
import translateX from 'utils/translateX';
import getValueFromFlattenObj from 'utils/getValueFromFlattenObj';
import entitiesService from 'services/entitiesService';
import useZustand from 'hooks/useZustand';
import { useNavigate } from 'react-router-dom';
import appService from 'services/appService';
import ValidationSuffix from 'components/jarvislyComponents/ValidationSuffix.jsx';
import { createImportToken, buildUrlWithToken } from 'utils/crossProfileImport';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const NIPC_MASK = '999 999 999';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const SimpleNipc = ({
  name,
  subdocumentField,
  disabled,
  onBlur,
  searchNipcByApi = false,
  unique = false,
  silentSearch = false,
  ...props
}) => {

  // 1. PROPS, CONTEXTS & GLOBAL STATE
  const {
    locked,
    setFormCnpjState,
    fieldsWithError,
    disabledFields,
    handleCnpjChange,
    data,
    form,
    setUniqueEntity,
    reloadEntirePage,
    formTmpState,
    removeFromFieldsWithError,
    goFocus,
    setFormZipState,
  } = useSimpleFormContext();

  const navigate = useNavigate();

  const storedModuleConfig = useZustand('module', 'config');
  const storedCurrentPage = useZustand('module', 'currentPage');

  const [modal, contextHolder] = Modal.useModal();

  // 2. HOOKS & REFS
  const elementProps = useFormElement(name, subdocumentField);
  const { elementRef, fullFieldName, flattenFieldName } = elementProps;

  const currentValueRaw = Form.useWatch(fullFieldName, form);
  const currentValue =
    typeof currentValueRaw === 'object' && currentValueRaw !== null
      ? currentValueRaw.value
      : currentValueRaw;

  const dataValueRaw = getValueFromFlattenObj(data, flattenFieldName);
  const dataValue =
    typeof dataValueRaw === 'object' && dataValueRaw !== null ? dataValueRaw.value : dataValueRaw;

  const nipcState = formTmpState?.cnpj?.[flattenFieldName] || null;
  const runOnce = useRef(false);
  const previousNipcRef = useRef(null);
  const needsClearStateRef = useRef(false);

  const {
    customValidationStatus: baseStatus,
    customValidationMessage: baseMessage,
    handleChange,
  } = useCustomValidationStatus(
    {
      ...props,
      ...elementProps,
      loading: !!nipcState?.searching,
    },
    isValidNipc
  );

  // 3. LOCAL VARIABLES I
  const isDisabled = disabled || locked || disabledFields?.includes(flattenFieldName);

  const { finalStatus, finalMessage } = useMemo(() => {
    const currentNipc = returnOnlyNumbers(currentValue);
    const savedNipc = returnOnlyNumbers(dataValue);

    if (nipcState?.searching) {
      return { finalStatus: 'validating', finalMessage: '' };
    }

    const ctxMessageKey = fieldsWithError?.[flattenFieldName];
    if (ctxMessageKey) {
      return { finalStatus: 'error', finalMessage: translateX(ctxMessageKey) };
    }

    if (!currentNipc) {
      return { finalStatus: '', finalMessage: '' };
    }

    if (currentNipc && !isValidNipc(currentNipc)) {
      return { finalStatus: 'error', finalMessage: translateX('invalid_nipc') };
    }

    if ((savedNipc && currentNipc === savedNipc) || nipcState?.apiFailure) {
      return { finalStatus: 'success', finalMessage: '' };
    }

    if (currentNipc && isValidNipc(currentNipc)) {
      return { finalStatus: 'success', finalMessage: '' };
    }

    return { finalStatus: baseStatus || '', finalMessage: baseMessage || '' };
  }, [currentValue, fieldsWithError, flattenFieldName, nipcState, baseStatus, baseMessage, dataValue]);

  // 4. LOCAL STATE

  // 5. EFFECTS
  React.useEffect(() => {
    if (runOnce.current) return;
    const currentNipc = returnOnlyNumbers(currentValue);
    const savedNipc = returnOnlyNumbers(dataValue);
    if (finalStatus === 'success' && savedNipc && currentNipc === savedNipc) {
      setFormCnpjState(flattenFieldName, { lastSearchFound: currentNipc });
      runOnce.current = true;
    }
  }, [finalStatus, currentValue, dataValue, flattenFieldName, setFormCnpjState]);

  // 6. HANDLERS
  const handleNipcChangeWrapper = useCallback(
    ev => {
      handleChange(ev);
      handleCnpjChange?.({
        ev,
        ...elementProps,
        value: ev?.target?.value ?? ev ?? '',
        subdocumentField,
      });
      needsClearStateRef.current = true;
    },
    [handleChange, handleCnpjChange, elementProps, subdocumentField]
  );

  const handleUniqueSearch = useCallback(
    async (nipcValue, parentOnBlur) => {
      const cleanNipc = returnOnlyNumbers(nipcValue);
      if (!isValidNipc(cleanNipc)) {
        setUniqueEntity?.(null);
        parentOnBlur?.();
        return;
      }

      const currentDataNipc = returnOnlyNumbers(
        typeof data?.nipc === 'object' ? data?.nipc?.value : data?.nipc
      );
      if (cleanNipc === currentDataNipc) {
        parentOnBlur?.();
        return;
      }

      if (nipcState?.lastSearchFound === cleanNipc) {
        parentOnBlur?.();
        return;
      }

      try {
        if (!silentSearch) {
          appService.message({
            flag: 'i',
            text: 'checking_document_duplicate',
            key: 'checkDocumentDuplicate',
            duration: 0,
          });
        }

        const foundEntity = await entitiesService.searchByDocument({
          key: 'nipc',
          value: cleanNipc,
        });

        if (!silentSearch) appService.messageDestroy('checkDocumentDuplicate');

        if (foundEntity) {
          const entityName = foundEntity.name || foundEntity.fullName || 'Empresa';
          let userAction = null;

          modal.confirm({
            title: translateX('document_already_exists'),
            content: translateX('document_already_exists_message', { name: entityName }),
            okText: translateX('yes_load_data'),
            cancelText: translateX('no_keep_current'),
            focusTriggerAfterClose: false,
            afterClose: () => {
              if (userAction === 'canceled') {
                elementRef?.current?.focus?.();
              } else if (userAction === 'confirmed') {
                goFocus?.('name');
              }
            },
            onOk: () => {
              if (reloadEntirePage) {
                const moduleUrl = storedModuleConfig?.url;
                const pageEndpoint = storedCurrentPage?.routing?.endpoint || '';
                if (moduleUrl && foundEntity._id) {
                  const newEndpoint = pageEndpoint.replace(':_id', foundEntity._id);
                  let newUrl = `${moduleUrl}${newEndpoint}`;
                  const currentFilter =
                    storedCurrentPage?.pagination?.filter ||
                    (storedCurrentPage?.api?.query?.filter
                      ? JSON.parse(storedCurrentPage.api.query.filter)
                      : null);
                  const targetProfile = currentFilter?.entityProfile || [];
                  const sourceProfile =
                    foundEntity.dataProfile || foundEntity.entityProfile || [];
                  const needsImportToken =
                    targetProfile.length > 0 &&
                    !targetProfile.some(p => sourceProfile.includes(p));
                  if (needsImportToken) {
                    const token = createImportToken({
                      entityId: foundEntity._id,
                      sourceProfile,
                      targetProfile,
                    });
                    newUrl = buildUrlWithToken(newUrl, token);
                  }
                  navigate(newUrl, { replace: true });
                  setUniqueEntity?.(null);
                }
              } else {
                const currentFormValues = form.getFieldsValue(true);
                const { _id: entityId, _metadata, ...entityFields } = foundEntity;
                const mergedFormData = {
                  ...entityFields,
                  entityId,
                  avatar: currentFormValues?.avatar,
                  backColor: currentFormValues?.backColor,
                  foreColor: currentFormValues?.foreColor,
                };
                form.setFieldsValue(mergedFormData);

                const filledFields = ['name', 'fullName', 'phone.value', 'email.value'];
                filledFields.forEach(field => removeFromFieldsWithError?.(field));

                setUniqueEntity?.({
                  _id: entityId,
                  documentKey: 'nipc',
                  documentValue: cleanNipc,
                  data: foundEntity,
                });
                userAction = 'confirmed';
              }

              setFormCnpjState?.(flattenFieldName, { lastSearchFound: cleanNipc });
            },
            onCancel: () => {
              userAction = 'canceled';
              const previousValue = previousNipcRef.current;
              const previousValueClean = returnOnlyNumbers(previousValue);
              form.setFieldsValue({
                nipc: previousValue ? { value: previousValue } : { value: '' },
              });
              setUniqueEntity?.(null);
              setFormCnpjState?.(flattenFieldName, {
                searching: false,
                found: false,
                data: null,
                lastSearchFound: previousValueClean || null,
              });
            },
          });
        } else {
          setUniqueEntity?.(null);
          setFormCnpjState?.(flattenFieldName, { lastSearchFound: cleanNipc });
          parentOnBlur?.();
        }
      } catch (error) {
        if (!silentSearch) appService.messageDestroy('checkDocumentDuplicate');
        setUniqueEntity?.(null);
        setFormCnpjState?.(flattenFieldName, { lastSearchFound: cleanNipc });
        parentOnBlur?.();
      }
    },
    [
      data?.nipc,
      form,
      modal,
      setUniqueEntity,
      reloadEntirePage,
      storedModuleConfig,
      storedCurrentPage,
      navigate,
      setFormCnpjState,
      flattenFieldName,
      elementRef,
      nipcState?.lastSearchFound,
      removeFromFieldsWithError,
      goFocus,
      silentSearch,
    ]
  );

  const handleExternalApiSearch = useCallback(
    async nipcValue => {
      const cleanNipcValue = returnOnlyNumbers(nipcValue);
      if (!isValidNipc(cleanNipcValue)) return;

      const currentDataNipc = returnOnlyNumbers(
        typeof data?.nipc === 'object' ? data?.nipc?.value : data?.nipc,
      );
      if (cleanNipcValue === currentDataNipc) return;
      if (nipcState?.lastSearchFound === cleanNipcValue) return;

      const msg = silentSearch
        ? { update: () => {}, close: () => {} }
        : await appService.message({
            flag: 'i',
            text: 'searching_nipc_external',
            key: 'searchNipcExternal',
            duration: 0,
          });

      try {
        setFormCnpjState?.(flattenFieldName, { searching: true });

        const result = await searchNipc(cleanNipcValue);

        if (result?.data?.success) {
          const pojo = result.data;
          const currentFormValues = form.getFieldsValue(true);
          const primaryActivity = pojo.activities?.find(a => a.isPrimary);
          const secondaryActivities = pojo.activities?.filter(a => !a.isPrimary) || [];

          // VIES API validates VAT but doesn't return NIPC, so use searchedNipc when NIPC is null
          const nipcValue = pojo.nipc || pojo.documents?.[0]?.value || cleanNipcValue;

          const formData = {
            entityType: 'company',
            dataOrigin: 'new_data',
            name: pojo.name || currentFormValues?.name,
            fullName: pojo.fullName || currentFormValues?.fullName,
            foundedAt: pojo.foundedAt || currentFormValues?.foundedAt,
            nipc: { value: nipcValue, countryCode: 'PT' },
            legalNature: pojo.legalNature
              ? { code: null, description: pojo.legalNatureRaw || pojo.legalNature }
              : currentFormValues?.legalNature,
            shareCapital: pojo.shareCapital ?? currentFormValues?.shareCapital,
            segment: pojo.segment || currentFormValues?.segment,
            mainActivity: primaryActivity
              ? { code: primaryActivity.code, description: primaryActivity.description }
              : currentFormValues?.mainActivity,
            additionalActivities:
              secondaryActivities.length > 0
                ? secondaryActivities.map(a => ({ code: a.code, description: a.description }))
                : currentFormValues?.additionalActivities,
            registrationStatus: pojo.registrationStatus
              ? { status: pojo.registrationStatus.status, isValid: pojo.registrationStatus.isValid }
              : currentFormValues?.registrationStatus,
            avatar: currentFormValues?.avatar,
            backColor: currentFormValues?.backColor,
            foreColor: currentFormValues?.foreColor,
            entityId: null,
            dataEnrichment: {
              isEnriched: true,
              enrichedAt: new Date().toISOString(),
              provider: pojo.provider || 'nifpt',
              status: 'found',
            },
          };

          // Address (NIF.pt returns address1, zip, city)
          const mainAddress = pojo.addresses?.[0];
          if (mainAddress?.address1 || mainAddress?.city) {
            formData.address = {
              address1: mainAddress.address1 || '',
              zip: mainAddress.zip || '',
              city: mainAddress.city || '',
              countryCode: 'PT',
              addressMetadata: { countryCode: 'PT', foundByApi: true, manual: false, apiFailure: false },
            };
            if (setFormZipState && mainAddress.zip) {
              setFormZipState('address.zip', {
                searching: false,
                apiFailure: false,
                lastSearchFound: mainAddress.zip,
                data: formData.address,
              });
            }
          }

          setUniqueEntity?.(null);
          form.setFieldsValue(formData);

          const filledFields = ['name', 'fullName', 'foundedAt'];
          filledFields.forEach(field => removeFromFieldsWithError?.(field));

          setFormCnpjState?.(flattenFieldName, {
            searching: false,
            lastSearchFound: cleanNipcValue,
            apiFailure: false,
          });

          msg.update({ flag: 's', text: 'nipc_found_by_api' });
        } else {
          setFormCnpjState?.(flattenFieldName, {
            searching: false,
            apiFailure: true,
            lastSearchFound: cleanNipcValue,
          });
          msg.update({ flag: 'w', text: 'nipc_not_found_title' });
        }
      } catch {
        msg.close();
        setFormCnpjState?.(flattenFieldName, {
          searching: false,
          apiFailure: true,
          lastSearchFound: cleanNipcValue,
        });
        if (!silentSearch) {
          appService.notification({
            key: 'nipcApiErrorNotificationKey',
            flag: 'w',
            title: translateX('nipc_api_error_title'),
            description: translateX('nipc_api_error_description'),
            duration: 8,
          });
        }
      }
    },
    [
      data?.nipc,
      form,
      flattenFieldName,
      setFormCnpjState,
      setFormZipState,
      nipcState?.lastSearchFound,
      removeFromFieldsWithError,
      setUniqueEntity,
      silentSearch,
    ],
  );

  const handleNipcBlurWrapper = async ev => {
    const value = ev?.target?.value ?? ev?.currentTarget?.value ?? ev ?? '';
    const cleanNipcValue = returnOnlyNumbers(value);
    const lastSearchedBeforeClear = nipcState?.lastSearchFound;

    if (needsClearStateRef.current) {
      needsClearStateRef.current = false;
      const valueActuallyChanged =
        !lastSearchedBeforeClear || lastSearchedBeforeClear !== cleanNipcValue;
      if (valueActuallyChanged) {
        previousNipcRef.current = lastSearchedBeforeClear || data?.nipc?.value || null;
        if (unique) setUniqueEntity?.(null);
        setFormCnpjState?.(flattenFieldName, { lastSearchFound: null, apiFailure: false });
      }
    }

    if (!isValidNipc(cleanNipcValue)) return;

    if (unique) {
      await handleUniqueSearch(value, async () => {
        if (searchNipcByApi) await handleExternalApiSearch(value);
      });
    } else if (searchNipcByApi) {
      await handleExternalApiSearch(value);
    }
  };

  // 7. LOCAL VARIABLES II

  // 8. UI
  return (
    <>
      {contextHolder}
      <SimpleFormItem
        {...props}
        name={fullFieldName}
        elementProps={elementProps}
        customValidationStatus={finalStatus}
        customValidationMessage={finalMessage}
        showFeedback={false}
      >
        <NipcInput
          ref={elementRef}
          id={flattenFieldName}
          onBlur={handleNipcBlurWrapper}
          onChange={handleNipcChangeWrapper}
          disabled={isDisabled}
          status={finalStatus}
          loading={nipcState?.searching || false}
        />
      </SimpleFormItem>
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default SimpleNipc;

// *******************************************************************************
// HELPERS
// *******************************************************************************

const NipcInput = React.forwardRef(
  (
    { loading, status, allowClear, prefix, suffix, onChange, onBlur, autoFocus, disabled, ...props },
    ref
  ) => {
    const stablePrefixRef = useRef(prefix ?? null);

    const staticSuffix = useMemo(
      () => <ValidationSuffix status={status || ''} loading={!!loading} extraSuffix={suffix} />,
      [status, loading, suffix]
    );

    const maskedValue = $applyMask(props.value);

    const onLocalChange = ev => {
      const digits = returnOnlyNumbers(ev?.target?.value);
      if ((digits?.length || 0) > 9) return;
      onChange && onChange(ev);
    };

    return (
      <Input
        {...props}
        ref={ref}
        value={maskedValue}
        onBlur={onBlur}
        autoFocus={autoFocus}
        prefix={stablePrefixRef.current}
        suffix={staticSuffix}
        disabled={disabled}
        onChange={onLocalChange}
        status={status || undefined}
      />
    );

    function $applyMask(val) {
      const raw = typeof val === 'object' && val !== null ? val.value : val;
      const digits = returnOnlyNumbers(raw);
      if (!digits) return '';
      return applyNumberMask(digits, NIPC_MASK) || '';
    }
  }
);
